import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import api, { formatApiError } from '../../services/api';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';

const DAY_OPTION_KEYS = [
  { value: 0, labelKey: 'sunday' },
  { value: 1, labelKey: 'monday' },
  { value: 2, labelKey: 'tuesday' },
  { value: 3, labelKey: 'wednesday' },
  { value: 4, labelKey: 'thursday' },
  { value: 5, labelKey: 'friday' },
  { value: 6, labelKey: 'saturday' },
];

const pad2 = (n) => String(n).padStart(2, '0');

const toTimeValue = (hour, minute) => `${pad2(hour)}:${pad2(minute)}`;

const parseTimeValue = (value) => {
  const [h, m] = String(value || '09:00').split(':');
  return {
    hour: Math.max(0, Math.min(23, parseInt(h, 10) || 0)),
    minute: Math.max(0, Math.min(59, parseInt(m, 10) || 0)),
  };
};

const ReminderScheduleModal = ({ open, onClose }) => {
  const { t } = useTranslation(['planning', 'common']);
  const [dayOfWeek, setDayOfWeek] = useState(5);
  const [timeValue, setTimeValue] = useState('09:00');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { toast, showSuccess, showError, hideToast } = useToast();

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getReminderSchedule();
        if (cancelled) return;
        setDayOfWeek(Number(data?.day_of_week ?? 5));
        setTimeValue(toTimeValue(data?.hour ?? 9, data?.minute ?? 0));
        setEnabled(data?.enabled !== false);
      } catch (err) {
        if (cancelled) return;
        const formatted = formatApiError(err);
        setError(formatted.generalError || t('failedToLoadReminderSchedule'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { hour, minute } = parseTimeValue(timeValue);
      await api.updateReminderSchedule({
        day_of_week: Number(dayOfWeek),
        hour,
        minute,
        enabled: !!enabled,
      });
      showSuccess(t('scheduleUpdated'), t('reminderScheduleSaved'));
    } catch (err) {
      const formatted = formatApiError(err);
      const message = formatted.generalError || t('failedToSaveReminderSchedule');
      setError(message);
      showError(t('saveFailed'), message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="teams-planning-page reminder-schedule-root" key="reminder-schedule">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-slate-900/40"
          />
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md border border-slate-200 overflow-hidden"
              onClick={(ev) => ev.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="reminder-schedule-title"
            >
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
                <h2 id="reminder-schedule-title" className="text-lg font-bold text-slate-900">
                  {t('planningReminderSchedule')}
                </h2>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  aria-label={t('common:close')}
                  title={t('common:close')}
                >
                  <span className="material-symbols-outlined text-[22px]">close</span>
                </button>
              </div>

              <form onSubmit={handleSave} className="p-5 space-y-4">
                {loading ? (
                  <p className="text-sm text-slate-500">{t('loadingSchedule')}</p>
                ) : (
                  <>
                    {error && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    <div>
                      <label
                        className="block text-sm font-medium text-slate-700 mb-1.5"
                        htmlFor="reminder-day"
                      >
                        {t('dayOfWeek')}
                      </label>
                      <select
                        id="reminder-day"
                        value={dayOfWeek}
                        onChange={(ev) => setDayOfWeek(Number(ev.target.value))}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      >
                        {DAY_OPTION_KEYS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-slate-700 mb-1.5"
                        htmlFor="reminder-time"
                      >
                        {t('time')}
                      </label>
                      <input
                        id="reminder-time"
                        type="time"
                        value={timeValue}
                        onChange={(ev) => setTimeValue(ev.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        required
                      />
                      <p className="mt-1.5 text-xs text-slate-500">{t('timesAreUtc')}</p>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!enabled}
                        onChange={(ev) => setEnabled(ev.target.checked)}
                        className="rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      {t('enabledWeeklyReminders')}
                    </label>
                  </>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading || saving}
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-60"
                  >
                    {saving ? t('common:saving') : t('common:save')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>

          <Toast toast={toast} onClose={hideToast} />
        </div>
      ) : null}
    </AnimatePresence>
  );
};

/** Icon button matching people-filters-toggle; opens reminder schedule. */
export function TeamsPlanningReminderScheduleButton({ onClick }) {
  const { t } = useTranslation('planning');
  return (
    <div className="teams-planning-page">
      <button
        type="button"
        className="people-filters-toggle reminder-schedule-toggle"
        onClick={onClick}
        aria-label={t('manageReminderSchedule')}
        title={t('planningReminderSchedule')}
      >
        <span className="material-symbols-outlined">schedule</span>
      </button>
    </div>
  );
}

export default ReminderScheduleModal;
